import { Component, FormEvent, HTMLAttributes, ReactNode, createContext } from 'react';

import classNames from 'classnames';

import { formDataLayer, formDataLayerGA4 } from '@anwb/data-layer';
import Notification, { NotificationProps } from '@anwb/notification';
import { FormContextType, FormElementsData, FormValidationFunction } from '@anwb/types';

import './styles/form.less';

export const FormContext = createContext<FormContextType>({
	handlerState: () => {},
	horizontal: false,
	registerComponent: () => {},
	unRegisterComponent: () => {},
	executeSubmit: () => {},
	autocompletion: 'on',
});

export type FormProps = Omit<
	HTMLAttributes<HTMLFormElement>,
	'children' | 'loading' | 'onSubmit'
> & {
	/** Whether the form is showing loading indication or not */
	loading?: boolean;
	/** Whether to render a form with a horizontal elements flow */
	horizontal?: boolean;
	/** The children to render inside the container */
	children?: ReactNode;
	/** The handler which can be called to submit the form */
	onSubmit?: (formData: unknown) => void;
	/** An optional class to provide additional styling */
	class?: string;
	/** The component name used for analytics */
	componentName?: string;
	/** You can provide an optional notification to display to the user, form error messages are handled by the form itself */
	notification?: NotificationProps;
	/** You can provide an optional error notification to display to the user */
	errorNotification?: NotificationProps;
	/** The handler which communicates the curent component's state to its parent component */
	handlerState?: (elementsData: FormElementsData) => void;
	/** You can provide an optional autocomplete value, default no autocomplete property will be passed to the form */
	autocompletion?: 'on' | 'off';
};

type FormState = {
	elementsToValidate: number;
	components: { [key: string]: FormValidationFunction };
	elementsData: FormElementsData;
	valid?: boolean;
};

class Form extends Component<FormProps, FormState> {
	mounted = false;

	static defaultProps = {
		loading: false,
		handlerState: undefined,
		onSubmit: undefined,
		notification: undefined,
		horizontal: false,
		autocompletion: undefined,
	};

	state = {
		elementsToValidate: 0,
		components: {},
		elementsData: {},
		valid: true,
	};

	element: HTMLFormElement | undefined = undefined;

	componentDidMount() {
		this.mounted = true;
	}

	handlerState = (elementsData: FormElementsData) => {
		const { handlerState } = this.props;

		if (JSON.stringify(elementsData) !== JSON.stringify(this.state.elementsData)) {
			this.setState(
				(prevState) => ({
					elementsData: {
						...prevState.elementsData,
						...elementsData,
					},
				}),
				() => {
					if (handlerState) handlerState(this.state.elementsData);
				}
			);
		}
	};

	executeSubmit = (elementData: FormElementsData) =>
		this.setState(
			({ elementsToValidate, elementsData }) => {
				const counter = elementsToValidate - 1;
				const elementsDataNew = {
					...elementsData,
					...elementData,
				};
				if (counter === 0) {
					const valid = this.element && this.element.checkValidity();

					return {
						elementsData: elementsDataNew,
						elementsToValidate: 0,
						valid,
					};
				}

				return { elementsData: elementsDataNew, elementsToValidate: counter };
			},
			() => {
				const { elementsToValidate, elementsData } = this.state;
				const { onSubmit } = this.props;

				if (elementsToValidate === 0 && onSubmit) {
					// eslint-disable-next-line @typescript-eslint/no-floating-promises
					new Promise((resolve) => {
						resolve(
							onSubmit({
								...this.state,
								elementsData: this.filterElementsData(elementsData),
							})
						);
					}).then((valid = this.element && this.element.checkValidity()) => {
						if (this.props.componentName) {
							formDataLayer({
								type: 'interaction',
								category: 'Formulier velden',
								action: `${this.props.componentName}-submit`,
								label: valid ? 'valid' : 'invalid',
							});
							formDataLayerGA4({
								event: 'user_interaction',
								type: 'form',
								component: this.props.componentName,
								field: '',
								action: 'submit',
								actionValue: valid ? 'valid' : `invalid`,
							});
						}
					});
				}
			}
		);

	handleSubmit = (event: FormEvent) => {
		const components = this.state.components as { [key: string]: () => void };
		const keys = Object.keys(components);

		event.preventDefault();

		if (document.activeElement) {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-call
			(document.activeElement as HTMLElement).blur();
		}

		this.setState({ elementsToValidate: keys.length }, () =>
			keys.forEach((component) => components[component]())
		);
	};

	registerComponent = ({
		reference,
		validation,
	}: {
		reference: string;
		validation: () => void;
	}) => {
		this.setState(({ components }) => ({
			components: { ...components, [reference]: validation },
		}));
	};

	unRegisterComponent = (reference: string) => {
		if (this.mounted) {
			this.setState(({ components }) => {
				const { [reference]: value, ...nextComponents } = components;

				return {
					components: nextComponents,
				};
			});
		}
	};

	componentWillUnmount() {
		this.mounted = false;
	}

	filterElementsData = (data: FormElementsData): FormElementsData =>
		Object.keys(data).reduce(
			(filteredData, key) =>
				data[key] === undefined
					? filteredData
					: {
							...filteredData,
							[key]: data[key],
					  },
			{}
		);

	renderNotification = ({ title, variant, closeButton, onClose, children }: NotificationProps) => (
		// eslint-disable-next-line react/react-in-jsx-scope
		<Notification variant={variant} title={title} closeButton={closeButton} onClose={onClose}>
			{children}
		</Notification>
	);

	render() {
		const {
			loading,
			children,
			notification,
			errorNotification,
			horizontal,
			componentName = 'form',
			handlerState: _,
			class: className,
			...props
		}: FormProps = this.props;
		const { valid } = this.state;

		const classNameGenerate = classNames(
			'PONCHO-form',
			className,
			horizontal && 'PONCHO-form--horizontal',
			loading && 'PONCHO-form--loading'
		);

		return (
			<FormContext.Provider
				value={{
					handlerState: this.handlerState,
					horizontal,
					registerComponent: this.registerComponent,
					unRegisterComponent: this.unRegisterComponent,
					executeSubmit: this.executeSubmit,
					autocompletion: this.props.autocompletion || 'on',
				}}
			>
				<form
					{...props}
					noValidate
					{...(this.props.autocompletion && {
						autoComplete: this.props.autocompletion,
						autocompletion: undefined, // why? this is not an existing attribute, needs documentation
					})}
					className={classNameGenerate}
					onSubmit={this.handleSubmit}
					ref={(reference) => {
						if (reference) {
							this.element = reference;
						}
					}}
				>
					<header className="PONCHO-form__header" aria-live="assertive">
						{notification
							? this.renderNotification(notification)
							: !valid &&
							  this.renderNotification(
									errorNotification || {
										title: 'Niet alle velden zijn correct ingevuld',
										variant: 'error',
									}
							  )}
					</header>
					{children}
				</form>
			</FormContext.Provider>
		);
	}
}

export default Form;
